import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ContactSection from "../components/ContactSection";

const Sustainability = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [activeTab, setActiveTab] = useState(0);

    const pillars = [
        {
            label: 'Energy Efficiency',
            description: 'We optimise energy consumption across our IT infrastructure and workplace environments using virtualisation, hybrid cloud architectures, and intelligent monitoring systems. By consolidating workloads and adopting energy-aware hardware, we reduce power consumption without compromising performance or reliability.',
            image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&auto=format&fit=crop&q=80'
        },
        {
            label: 'Waste Reduction',
            description: 'Solaris enforces responsible e-waste disposal and circular economy principles across all hardware lifecycle processes. We partner with certified recycling providers, extend equipment lifecycles through managed refresh programmes, and maintain digital-first operations to minimise physical waste generation at every level.',
            image: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=800&auto=format&fit=crop&q=80'
        },
        {
            label: 'Sustainable Commuting',
            description: 'Our hybrid workplace model significantly reduces commuting-related emissions across our workforce. We invest in remote collaboration infrastructure, flexible working policies, and digital meeting technologies that enable our teams to work effectively from anywhere - reducing travel dependency while improving work-life balance.',
            image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop&q=80'
        },
        {
            label: 'Carbon Footprint Reduction',
            description: 'We actively measure, report, and reduce our carbon footprint across Scope 1, 2, and 3 emissions. Through low-energy infrastructure architecture, cloud optimisation, and supplier alignment, Solaris pursues a structured decarbonisation roadmap with transparent annual reporting and measurable year-on-year reduction targets.',
            image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&auto=format&fit=crop&q=80'
        }
    ];

    const stats = [
        { value: '30%', label: 'Reduction in energy consumption year-over-year across internal IT infrastructure.', highlight: false },
        { value: '20%', label: 'Decrease in employee commuting emissions through hybrid workplace enablement.', highlight: true },
        { value: '70%', label: 'Office waste recycled annually through responsible disposal and digital-first processes.', highlight: true },
        { value: '80%', label: 'Vendors aligned with sustainable sourcing and compliance standards.', highlight: false },
    ];

    const impactCards = [
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" stroke="#2563eb" strokeWidth="1.8" /><path d="M12 6v6l4 2" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /></svg>
            ),
            title: 'Carbon Conscious',
            desc: 'We actively measure and reduce carbon emissions across our IT infrastructure services, promoting low-energy architecture and optimised resource usage.'
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            title: 'Energy Smart',
            desc: 'Through energy-efficient data centres, cloud optimisation, and intelligent monitoring, we reduce power consumption without compromising performance.'
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M23 7l-7 5 7 5V7z" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M1 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H1V5z" stroke="#2563eb" strokeWidth="1.8" /></svg>
            ),
            title: 'Zero Waste Future',
            desc: 'We adopt responsible e-waste disposal, hardware lifecycle management, and digital-first operations to minimise waste generation across all business functions.'
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><polyline points="17 6 23 6 23 12" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            title: 'Responsible Growth',
            desc: 'We prioritise ethical sourcing, sustainable partnerships, and long-term value creation for clients, communities, and stakeholders at every stage of growth.'
        }
    ];

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '520px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.76)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '140px 15px 100px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '28px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Our Commitment
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 24px', maxWidth: '860px'
                        }}>
                            Building Technology That<br />Respects Tomorrow
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.72)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '560px', margin: '0 auto 40px'
                        }}>
                            Sustainability is not a side initiative at Solaris - it is embedded in how we design infrastructure, manage operations, and partner with our clients to create lasting value.
                        </p>
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                color: '#fff', border: 'none',
                                padding: '14px 8px 14px 28px',
                                borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                            >
                                <span style={{ marginRight: '16px' }}>Learn More</span>
                                <span style={{
                                    background: '#fff', borderRadius: '50%', width: '38px', height: '38px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ── IMPACT STATEMENT - 4 CARDS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>IMPACT STATEMENT</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: '0', maxWidth: '600px', margin: '0 auto' }}>
                            Building Technology That Respects Tomorrow
                        </h2>
                    </div>

                    <div className="row g-4 mb-5">
                        {impactCards.map((card, i) => (
                            <div key={i} className="col-lg-3 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#bfdbfe' }}
                                    style={{
                                        padding: '32px 24px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default', transition: 'all 0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: '#eff6ff', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                                    }}>
                                        {card.icon}
                                    </div>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{card.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: 0 }}>{card.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>

                    {/* Pull quote */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        viewport={{ once: true }}
                        style={{ textAlign: 'center', padding: '48px 20px', maxWidth: '860px', margin: '0 auto' }}
                    >
                        <p style={{
                            fontSize: 'clamp(1.15rem, 2.2vw, 1.55rem)',
                            lineHeight: 1.75, fontWeight: 400,
                            color: '#94a3b8'
                        }}>
                            Our sustainability journey is guided by measurable progress,{' '}
                            <span style={{ color: '#2563eb', fontWeight: 700 }}>
                                not promises. From reducing energy consumption to cutting down waste and lowering emissions, we continuously improve how our managed IT services
                            </span>
                            {' '}and enterprise infrastructure operate. These metrics reflect our commitment to building a greener, more responsible digital future.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── KEY PILLARS - TABBED ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ marginBottom: '52px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>KEY PILLARS</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: 0 }}>
                            How We Reduce<br />Environmental Impact
                        </h2>
                    </div>

                    <div className="row g-5 align-items-start">
                        {/* Left: tab list */}
                        <div className="col-lg-5">
                            <div style={{ marginBottom: '32px' }}>
                                {pillars.map((p, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveTab(i)}
                                        style={{
                                            display: 'block', width: '100%', textAlign: 'left',
                                            padding: '18px 24px',
                                            background: activeTab === i ? '#fff' : 'transparent',
                                            border: 'none',
                                            borderLeft: activeTab === i ? '3px solid #2563eb' : '3px solid transparent',
                                            borderRadius: activeTab === i ? '0 12px 12px 0' : '0',
                                            cursor: 'pointer',
                                            color: activeTab === i ? '#0f172a' : '#94a3b8',
                                            fontWeight: activeTab === i ? 700 : 500,
                                            fontSize: '1rem',
                                            boxShadow: activeTab === i ? '0 4px 20px rgba(0,0,0,0.06)' : 'none',
                                            transition: 'all 0.25s',
                                            marginBottom: '4px'
                                        }}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <motion.p
                                key={activeTab}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.85, paddingLeft: '8px' }}
                            >
                                {pillars[activeTab].description}
                            </motion.p>
                        </div>

                        {/* Right: image */}
                        <div className="col-lg-7">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.4 }}
                                style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}
                            >
                                <img
                                    src={pillars[activeTab].image}
                                    alt={pillars[activeTab].label}
                                    style={{ width: '100%', height: '380px', objectFit: 'cover', display: 'block' }}
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── METRICS - DARK ── */}
            <section style={{ padding: '100px 0', background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 10% 50%, rgba(37,99,235,0.12) 0%, transparent 55%), radial-gradient(ellipse at 90% 20%, rgba(14,165,233,0.1) 0%, transparent 55%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        {/* Left */}
                        <div className="col-lg-4">
                            <motion.div
                                initial={{ opacity: 0, x: -24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                                    borderRadius: '50px', padding: '5px 16px', marginBottom: '24px'
                                }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                                    <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                        Vision & Impact
                                    </span>
                                </div>
                                <h2 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                                    lineHeight: 1.2, marginBottom: '28px'
                                }}>
                                    Measured.<br />Transparent.<br />Accountable.
                                </h2>
                                <div style={{ borderRadius: '16px', overflow: 'hidden', marginTop: '8px' }}>
                                    <img
                                        src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&auto=format&fit=crop&q=80"
                                        alt="Sustainable office"
                                        style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                                    />
                                </div>
                            </motion.div>
                        </div>

                        {/* Right: stat grid */}
                        <div className="col-lg-8">
                            <div className="row g-3">
                                {stats.map((s, i) => (
                                    <div key={i} className="col-6">
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: i * 0.1 }}
                                            viewport={{ once: true }}
                                            style={{
                                                padding: '36px 28px',
                                                borderRadius: '16px',
                                                background: s.highlight
                                                    ? 'linear-gradient(135deg, #2563eb, #0ea5e9)'
                                                    : 'rgba(255,255,255,0.06)',
                                                border: s.highlight ? 'none' : '1px solid rgba(255,255,255,0.1)',
                                                height: '100%'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: 'clamp(2.2rem, 4vw, 3.2rem)',
                                                fontWeight: 800,
                                                color: '#fff',
                                                lineHeight: 1,
                                                marginBottom: '16px'
                                            }}>
                                                {s.value}
                                            </div>
                                            <p style={{
                                                color: s.highlight ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)',
                                                fontSize: '0.88rem', lineHeight: 1.7, margin: 0
                                            }}>
                                                {s.label}
                                            </p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default Sustainability;
